import React from 'react';
import Chart from 'react-apexcharts';
import { Box } from '@mui/material';

const SPCChart = ({
  data = [],
  categories = [],
  ucl = null,
  lcl = null,
  mean = null,
  height = 220,
}) => {
  // แปลงเป็นตัวเลขเฉพาะค่าที่ไม่ใช่ null, undefined หรือ ''
  const toFiniteNumber = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const validUcl = toFiniteNumber(ucl);
  const validLcl = toFiniteNumber(lcl);
  const validMean = toFiniteNumber(mean);

  const hasUcl = validUcl !== null;
  const hasLcl = validLcl !== null;
  const hasMean = validMean !== null;

  // ต้องมีครบทั้ง 3 ค่าจึงจะแสดง background bands
  const hasAllControlLimits = hasUcl && hasLcl && hasMean;

  const numericData = Array.isArray(data)
    ? data
        .map(toFiniteNumber)
        .filter((value) => value !== null)
    : [];

  /*
   * หาแกน Y จากค่าที่มีจริง
   * ไม่ใช้ null ในการคำนวณ เพราะ Number(null) จะกลายเป็น 0
   */
  const yAxisValues = [
    ...numericData,
    ...(hasUcl ? [validUcl] : []),
    ...(hasLcl ? [validLcl] : []),
    ...(hasMean ? [validMean] : []),
  ];

  const rawYMin =
    yAxisValues.length > 0 ? Math.min(...yAxisValues) : 0;

  const rawYMax =
    yAxisValues.length > 0 ? Math.max(...yAxisValues) : 1;

  const range = rawYMax - rawYMin;
  const pad = range > 0 ? Math.max(range * 0.1, 1) : 1;

  const yMin = Math.floor(rawYMin - pad);
  const yMax = Math.ceil(rawYMax + pad);

  const sigma = hasAllControlLimits
    ? (validUcl - validMean) / 3
    : null;

  const yAnnotations = [];

  /*
   * Background bands จะแสดงเฉพาะเมื่อมี UCL, LCL และ Mean ครบ
   * และลำดับค่าถูกต้อง
   */
  if (
    hasAllControlLimits &&
    validLcl <= validMean - 2 * sigma &&
    validMean - 2 * sigma <= validMean + 2 * sigma &&
    validMean + 2 * sigma <= validUcl
  ) {
    yAnnotations.push(
      {
        y: yMin,
        y2: validLcl,
        fillColor: '#f44336',
        opacity: 0.04,
        borderWidth: 0,
      },
      {
        y: validLcl,
        y2: validMean - 2 * sigma,
        fillColor: '#ff9800',
        opacity: 0.04,
        borderWidth: 0,
      },
      {
        y: validMean - 2 * sigma,
        y2: validMean + 2 * sigma,
        fillColor: '#4caf50',
        opacity: 0.04,
        borderWidth: 0,
      },
      {
        y: validMean + 2 * sigma,
        y2: validUcl,
        fillColor: '#ff9800',
        opacity: 0.04,
        borderWidth: 0,
      },
      {
        y: validUcl,
        y2: yMax,
        fillColor: '#f44336',
        opacity: 0.04,
        borderWidth: 0,
      }
    );
  }

  // UCL แสดงเฉพาะเมื่อ ucl มีค่าจริง
  if (hasUcl) {
    yAnnotations.push({
      y: validUcl,
      borderColor: '#f44336',
      strokeDashArray: 5,
      borderWidth: 1.5,
      label: {
        text: `UCL=${validUcl.toFixed(2)}`,
        position: 'right',
        textAnchor: 'start',
        offsetX: 6,
        borderColor: 'transparent',
        borderWidth: 0,
        style: {
          color: '#f44336',
          background: '#0d0d0d',
          fontFamily: "'CPF Imm Sook', sans-serif",
          fontSize: '11px',
          fontWeight: 'bold',
          padding: {
            top: 2,
            bottom: 2,
            left: 4,
            right: 4,
          },
        },
      },
    });
  }

  // Mean แสดงเฉพาะเมื่อ mean มีค่าจริง
  if (hasMean) {
    yAnnotations.push({
      y: validMean,
      borderColor: '#4caf50',
      strokeDashArray: 5,
      borderWidth: 1.5,
      label: {
        text: `X̄=${validMean.toFixed(2)}`,
        position: 'right',
        textAnchor: 'start',
        offsetX: 6,
        borderColor: 'transparent',
        borderWidth: 0,
        style: {
          color: '#4caf50',
          background: '#0d0d0d',
          fontFamily: "'CPF Imm Sook', sans-serif",
          fontSize: '11px',
          fontWeight: 'bold',
          padding: {
            top: 2,
            bottom: 2,
            left: 4,
            right: 4,
          },
        },
      },
    });
  }

  // LCL แสดงเฉพาะเมื่อ lcl มีค่าจริง
  if (hasLcl) {
    yAnnotations.push({
      y: validLcl,
      borderColor: '#f44336',
      strokeDashArray: 5,
      borderWidth: 1.5,
      label: {
        text: `LCL=${validLcl.toFixed(2)}`,
        position: 'right',
        textAnchor: 'start',
        offsetX: 6,
        borderColor: 'transparent',
        borderWidth: 0,
        style: {
          color: '#f44336',
          background: '#0d0d0d',
          fontFamily: "'CPF Imm Sook', sans-serif",
          fontSize: '11px',
          fontWeight: 'bold',
          padding: {
            top: 2,
            bottom: 2,
            left: 4,
            right: 4,
          },
        },
      },
    });
  }

  const options = {
    chart: {
      type: 'line',
      background: '#111',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
      fontFamily: "'CPF Imm Sook', sans-serif",
    },

    colors: ['#5b9bd5'],

    stroke: {
      curve: 'straight',
      width: 2,
    },

    markers: {
      size: 5,
      colors: ['#5b9bd5'],
      strokeColors: '#5b9bd5',
      strokeWidth: 0,
      hover: { sizeOffset: 0 },
    },

    grid: {
      show: true,
      borderColor: '#2a2a2a',
      strokeDashArray: 4,
      padding: {
        top: 0,
        bottom: 0,
        left: 10,
        right: 90,
      },
    },

    xaxis: {
      categories,
      labels: {
        style: {
          colors: '#888',
          fontFamily: "'CPF Imm Sook', sans-serif",
          fontSize: '12px',
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },

    yaxis: {
      min: yMin,
      max: yMax,
      tickAmount: 6,
      forceNiceScale: false,
      labels: {
        style: {
          colors: '#888',
          fontFamily: "'CPF Imm Sook', sans-serif",
          fontSize: '12px',
        },
        formatter: (value) => Number(value).toFixed(0),
      },
    },

    annotations: {
      yaxis: yAnnotations,
    },

    tooltip: {
      enabled: false,
    },

    legend: {
      show: false,
    },

    theme: {
      mode: 'dark',
    },
  };

  const hasData =
    Array.isArray(data) &&
    data.some((value) => toFiniteNumber(value) !== null);

  return (
    <Box sx={{ width: '100%' }}>
      {!hasData ? (
        <Box
          sx={{
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              color: '#444',
              fontFamily: "'CPF Imm Sook', sans-serif",
              fontSize: 14,
            }}
          >
            No data
          </span>
        </Box>
      ) : (
        <Chart
          options={options}
          series={[
            {
              name: 'Weight',
              data,
            },
          ]}
          type="line"
          height={height}
        />
      )}
    </Box>
  );
};

export default SPCChart;